import { html, TemplateResult, LitElement } from 'lit';
import { customElement, property, queryAssignedElements } from 'lit/decorators.js';
import globalStyles from './styles/outline-main-header.global.css?inline';
import encapsulatedStyles from './styles/outline-main-header.encapsulated.css?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import '../outline-container/outline-container';
import {
  screenSizeMap,
} from '../../controllers/screen-size-controller';
import '../outline-svg/outline-svg';
import { OutlineSearch } from '../outline-search/outline-search';
import { debounce } from '../../utilities/debounce';
/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-main-header';

/**
 * The Outline Main Header component
 *
 * A reusable LitElement component for displaying the main header.
 *
 * @element outline-main-header
 * @extends LitElement
 */
@customElement(componentName)
export class OutlineMainHeader extends LitElement {
  resizeObserver: ResizeObserver;
  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  @queryAssignedElements({ slot: 'nav' })
  mainNavRef: Array<HTMLElement>
  
  @property({type: Boolean, attribute: 'scroll-lock', reflect: true})
  scrollLock: boolean;
  
  @property({type: Boolean, reflect: true})
  mobileMenuOpen: boolean = false;

  @property({type: Boolean, reflect: true})
  desktopSearchOpen: boolean = false;

  @property({ type: Boolean })
  isMobile: boolean;

  @property({ type: Number })
  scrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.resetAtScreen();
    });
    window.addEventListener('keydown', (e: KeyboardEvent) =>
    this.handleEscape(e)
  );
  window.addEventListener('click', (e: Event) =>
    this.windowClickAwayHandler(e)
  );
  window.addEventListener(
    'scroll',
    debounce(() => this.handleScroll(), 300)
  );
  window.addEventListener(
    'resize',
    debounce(() => this.handleScroll(), 300)
  );

  }
  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }

  
  firstUpdated() {
    const self = this.shadowRoot?.querySelector('.main-header') as HTMLElement;
    this.resizeObserver.observe(self);
  }

  render(): TemplateResult {
    return html`

    <header class="main-header">
      <slot name="skip-link"></slot>
      <outline-container>
          ${this.isMobile 
            ? this.mobileTemplate()
            : this.desktopTemplate()
          }
      </outline-container>
    </header>
    `;
  }

  desktopTemplate() {
    return html `
        <div class="main-header--content">
          <div class="main-header--logo">
            <a href="/" aria-label="return to homepage">
              <slot name="logo"></slot>
            </a>
          </div>
          <div class="main-header--main">
            <div class="main-header--top-content">
              <div class="main-header--utility-nav">
                <slot name="utility"></slot>
              </div>
              <div class="main-header--supplemental-nav">
                <slot name="supplemental"></slot>
              </div>
              <div class="main-header--search-wrapper">
                <div class="main-header--search ${this.desktopSearchOpen ? 'open' : ''}" aria-hidden=${this.desktopSearchOpen ? true : false}>
                  <slot name="search"></slot>
                </div>
                <button
                class="main-header--search-button"
                @click=${this.toggleDesktopSearch}
                aria-label="open search"
                aria-haspopup="true"
                aria-expanded=${this.desktopSearchOpen}
                >
                <span class="main-header--search-label ${this.desktopSearchOpen ? 'visually-hidden' : ''}" >Search</span>
                <outline-svg name="${this.desktopSearchOpen? 'cross' :'search'}" size="${this.desktopSearchOpen? '35px' :'30px'}" svg-fill="currentColor"></outline-svg>
              </button>
            </div>
            </div>
            <div class="main-header--bottom-content">
              <div class="main-header--main-nav">
                <slot name="nav"></slot>
              </div>
              <div class="main-header--cta">
                <slot name="cta"></slot>
              </div>
            </div>
          </div>
        </div>
    `
  }

  mobileTemplate() {
    return html`
    <div>
      <div class="main-header-mobile--content">
        <div class="main-header-mobile--top-content">
          <div class="main-header-mobile--logo">
            <a href="/" aria-label="return to homepage">
              <slot name="logo"></slot>
            </a>
          </div>
          <div class="main-header-mobile--button-wrapper">
            <button class="main-header-mobile--menu-button"
              @click=${this.mobileOpenClickHandler}
              aria-label="open menu"
              aria-haspopup="true"
              aria-expanded=${this.mobileMenuOpen}
            >
              <outline-svg name="${this.mobileMenuOpen ? 'cross':  'hamburger'}" size="30px" svg-fill="var(--bjc-blue)"></outline-svg>
            </button>
          </div>
        </div>
        <div class="main-header-mobile--main-content ${this.mobileMenuOpen ? 'open' : ''}">
          <div class="main-header-mobile--search">
            <slot name="search"></slot>
          </div>
          <div class="main-header-mobile--nav-wrapper">
            <slot name="nav"></slot>
          </div>
          <div class="main-header-mobile--supplemental-nav">
            <slot name="supplemental"></slot>
          </div>
          <div class="main-header-mobile--cta">
            <slot name="cta"></slot>
          </div>
          <div class="main-header-mobile--utility-nav">
            <slot name="utility"></slot>
          </div>
        </div>
      </div>
    </div>
    `
  }

  mobileOpenClickHandler() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.scrollLock = !this.scrollLock;
    this.toggleBodyLock();
  }

  toggleBodyLock() {
    const bodyLocked = this.checkBodyScrollLocked();
    if (!bodyLocked && this.scrollLock) {
      document.body.classList.add('mobile-scroll-locked');
    } else {
      document.body.classList.remove('mobile-scroll-locked');
    }
  }
  checkBodyScrollLocked() {
    return document.body.classList.contains('mobile-scroll-locked');
  }

  resetAtScreen() {
    const currentlyMobile = window.innerWidth < (screenSizeMap.get('xl') as number);
    if(this.isMobile !== currentlyMobile) {
      this.isMobile = window.innerWidth < (screenSizeMap.get('xl') as number);
      this.desktopSearchOpen = false;
      this.scrollLock = false;
      this.mobileMenuOpen = false;
      if (this.isMobile) {
        this.cleanupOpenMobileElements();
      } else {
        this.cleanupOpenDesktopElements();
      }
    }
  }

  toggleDesktopSearch() {
    this.desktopSearchOpen = !this.desktopSearchOpen;
    const search = this.querySelector('outline-search') as OutlineSearch;
    const input = document.querySelector('#header-search-query') as HTMLInputElement;
    if (this.desktopSearchOpen) {
      search.trapFocus = true;
      input.focus();      
    } else {
      search.trapFocus = false;
    }
  }
  handleEscape(e: KeyboardEvent) {
    if (e.key !== 'Escape') {
      //bail
      return;
    } else {
      if (this.isMobile) {
        this.cleanupOpenMobileElements();
      } else {
        this.cleanupOpenDesktopElements();
      }
    }
  }

  cleanupOpenDesktopElements(){
    const openFlyoutElements = this.mainNavRef[0]?.shadowRoot?.querySelectorAll('[open]')
    if(openFlyoutElements?.length) {
      openFlyoutElements.forEach(el => el.removeAttribute('open'))
    }
    this.desktopSearchOpen = false;
    this.scrollLock = false;
  }
  
  cleanupOpenMobileElements() {
    const openElements = this.mainNavRef[0]?.shadowRoot?.querySelectorAll('[open]');
    const flownawayElements = this.mainNavRef[0]?.shadowRoot?.querySelectorAll('.flyaway');
    
    if(openElements?.length) {
      openElements.forEach(el => el.removeAttribute('open'));
    }
    if (flownawayElements?.length) {
      flownawayElements.forEach(el => el.classList.remove('flyaway'));
    }
    this.mobileMenuOpen = false;
    this.scrollLock = false;
  }

  windowClickAwayHandler(e: Event) {
    const clickedElement = e.target as HTMLElement;
    if (
      !this.isMobile &&
      clickedElement.nodeName !== 'OUTLINE-NAV' &&
      clickedElement.nodeName !== 'OUTLINE-ICON' &&
      clickedElement.nodeName !== 'LABEL' &&
      clickedElement.nodeName !== 'INPUT' &&
      clickedElement.nodeName !== 'OUTLINE-MAIN-HEADER' &&
      !clickedElement.classList.contains('form-item')
    ) {      
      this.cleanupOpenDesktopElements();
    }
  }

  handleScroll(){
    const scrolledDown = this.scrollY < window.scrollY;
    const scrolledUP = this.scrollY > window.scrollY
    
    if (this.isMobile) {
      return;
    }
    if (scrolledDown && window.scrollY < 300) {
      return 
    }
    if (scrolledUP && window.scrollY < (this.scrollY - 10)) {
      this.setTopVar('0px')
    }
    
    if (scrolledDown && window.scrollY > (this.scrollY + 10)) {
      this.setTopVar('-200px')
    }
    return (this.scrollY = window.scrollY);
  }

  setTopVar(topValue: string) {
    this.style.setProperty('--top-position', topValue);
  }
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineMainHeader;
  }
}
